import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { listTeamMembers } from "@/lib/api";

const ADMIN_INITIALS = ["LH", "TC", "KH"];

export default function DesktopLogin() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const existing = localStorage.getItem("desktop_user_id");
    if (existing) { navigate("/"); return; }
    listTeamMembers().then(data => {
      setMembers(data.filter(m => ADMIN_INITIALS.includes(m.initials)));
    }).catch(() => {});
  }, [navigate]);

  const handleLogin = async () => {
    if (!selected) return;
    setChecking(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-desktop`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ initials: selected.initials, password })
      });
      if (!resp.ok) throw new Error();
      localStorage.setItem("desktop_user_id", selected.id);
      localStorage.setItem("desktop_user_name", selected.name);
      localStorage.setItem("desktop_user_initials", selected.initials);
      navigate("/");
    } catch {
      setError("Incorrect password");
      toast.error("Incorrect password");
    } finally { setChecking(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ background: "var(--bg)" }}>
      <img src="/hps-login-logo.png" alt="Hammond Property Solutions" className="h-48 object-contain mb-6" style={{ mixBlendMode: "multiply" }} />

      {!selected ? (
        <>
          <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>Select your account to continue</p>
          <div className="w-full max-w-sm space-y-2">
            {members.map(m => (
              <button
                key={m.id}
                onClick={() => { setSelected(m); setPassword(""); setError(""); }}
                className="w-full flex items-center gap-3 p-4 rounded-xl bg-white border transition-all hover:shadow-md active:scale-[0.98]"
                style={{ borderColor: "var(--border)" }}
                data-testid={`desktop-login-${m.initials}`}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                  style={{ backgroundColor: m.color || "#64748b" }}
                >
                  {m.initials}
                </div>
                <span className="font-medium text-base" style={{ color: "var(--text-primary)" }}>{m.name}</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-6">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
              style={{ backgroundColor: selected.color || "#64748b" }}
            >
              {selected.initials}
            </div>
            <div>
              <p className="font-semibold" style={{ color: "var(--text-primary)" }}>{selected.name}</p>
              <button onClick={() => { setSelected(null); setPassword(""); setError(""); }} className="text-xs" style={{ color: "var(--primary)" }}>
                Switch account
              </button>
            </div>
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); handleLogin(); }}
            autoComplete="on"
          >
            {/* Hidden username for Chrome autofill */}
            <input type="text" name="username" autoComplete="username" value={selected.initials} readOnly className="hidden" />

            <div className="relative mb-4">
              <Input
                type={showPw ? "text" : "password"}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                placeholder="Enter password"
                className="h-12 rounded-xl pr-10 text-base"
                style={{ border: error ? "2px solid var(--danger)" : "1px solid var(--border)" }}
                autoFocus
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: "var(--text-muted)" }}
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {error && <p className="text-xs mb-3" style={{ color: "var(--danger)" }}>{error}</p>}

            <Button
              type="submit"
              className="w-full h-12 rounded-xl text-base font-semibold btn-primary"
              data-testid="login-submit"
            >
              Sign In
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}
